import React from "react";
import { Input } from "../../../../../components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/select";
import { Textarea } from "../../../../../components/textarea";
import { FormErrorText, RequiredLabel } from "../../../../../components/form";
import { DISCOUNT_TYPES } from "../../data/merchantCouponEditor.constants";
import { MerchantCouponUIState } from "../../data/merchantCouponEditor.data";
import type { MerchantCouponValidationErrors } from "../../schemas/merchantCouponEditor.schema";

interface CouponDetailsSectionProps {
  coupon: MerchantCouponUIState;
  onFieldChange: (field: keyof MerchantCouponUIState, value: any) => void;
  isCreating: boolean;
  errors: MerchantCouponValidationErrors;
}

/* Spec from Figma HTML export (inline styles) – exact hex/pixel values */
const labelClass =
  "text-[#75757D] dark:text-gray-400 text-[14px] font-normal font-['Nunito'] leading-[21px]";
const inputOutline = "rounded-[8px] border border-[#E2E8F0] dark:border-gray-600";
const inputText =
  "text-[14px] font-normal font-['Segoe_UI'] bg-white dark:bg-gray-900 text-[#0A0A0A] dark:text-gray-100 placeholder:text-[#0A0A0A]/50 dark:placeholder:text-gray-400 disabled:opacity-50";

export const CouponDetailsSection: React.FC<CouponDetailsSectionProps> = ({
  coupon,
  onFieldChange,
  isCreating,
  errors,
}) => {
  const discountSymbol = coupon.discount_type === "percentage" ? "%" : "$";

  return (
    <div className="self-stretch p-[23.8px] bg-white dark:bg-gray-800 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] dark:outline-gray-600 flex flex-col gap-6">
      {/* Header – pb 8px, border 0.8px #00C49A, title 18px #004232 line-height 27px */}
      <div className="self-stretch pb-2 border-b-[0.80px] border-[#00C49A] dark:border-emerald-500 flex flex-col">
        <h2 className="text-[#004232] dark:text-emerald-100 text-[18px] font-normal font-['Nunito'] leading-[27px]">
          Coupon Details
        </h2>
      </div>

      <div className="self-stretch flex flex-col gap-5">
        {/* Coupon Title – gap 8px; input pt 10.6 pb 11.6 px 15.8, h 42.6 */}
        <div className="self-stretch flex flex-col gap-2">
          <RequiredLabel htmlFor="coupon-title" className={labelClass} required>
            Coupon Title
          </RequiredLabel>
          <Input
            id="coupon-title"
            value={coupon.coupon_title}
            onChange={(e) => onFieldChange("coupon_title", e.target.value)}
            disabled={isCreating}
            placeholder="e.g., 20% Off All Pizza Orders"
            className={`${inputOutline} ${inputText} h-[42.6px] pt-[10.6px] pb-[11.6px] px-[15.8px]`}
          />
          <FormErrorText message={errors.coupon_title} />
        </div>

        {/* Description – gap 8px; textarea pt 9.8 pb 51.8 px 15.8, placeholder line-height 21 */}
        <div className="self-stretch flex flex-col gap-2 pb-[5.6px]">
          <RequiredLabel htmlFor="coupon-description" className={labelClass} required>
            Description
          </RequiredLabel>
          <Textarea
            id="coupon-description"
            value={coupon.description}
            onChange={(e) => onFieldChange("description", e.target.value)}
            disabled={isCreating}
            placeholder="Describe what customers get with this coupon..."
            className={`${inputOutline} ${inputText} min-h-[120px] pt-[9.8px] pb-[51.8px] px-[15.8px] resize-none leading-[21px]`}
          />
          <FormErrorText message={errors.description} />
        </div>

        {/* Row – gap 16px; each flex-1, gap 8 */}
        <div className="self-stretch flex flex-col md:flex-row gap-4">
          {/* Discount Type – px 20 py 9.8, h 42.6; value #0A0A0A line-height 18.4 */}
          <div className="flex-1 flex flex-col gap-2">
            <RequiredLabel htmlFor="coupon-discount-type" className={labelClass} required>
              Discount Type
            </RequiredLabel>
            <Select
              value={coupon.discount_type}
              onValueChange={(value) => onFieldChange("discount_type", value)}
              disabled={isCreating}
            >
              <SelectTrigger
                id="coupon-discount-type"
                className={`${inputOutline} ${inputText} h-[42.6px] px-5 py-[9.8px] [&>span]:text-[#0A0A0A] dark:[&>span]:text-gray-100 [&>span]:text-[14px] [&>span]:leading-[18.4px]`}
              >
                <SelectValue placeholder="Select discount type" />
              </SelectTrigger>
              <SelectContent>
                {DISCOUNT_TYPES.map((type) => (
                  <SelectItem
                    key={type.value}
                    value={type.value}
                  >
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormErrorText message={errors.discount_type} />
          </div>

          {/* Discount Value – h 42.6, px 15.8/16.8; suffix #6E7670 14px leading 21 */}
          <div className="flex-1 flex flex-col gap-2">
            <RequiredLabel htmlFor="coupon-discount-value" className={labelClass} required>
              Discount Value
            </RequiredLabel>
            <div className="relative self-stretch">
              <Input
                id="coupon-discount-value"
                type="number"
                value={coupon.discount_value || ""}
                onChange={(e) => onFieldChange("discount_value", parseFloat(e.target.value) || 0)}
                disabled={isCreating}
                placeholder="0"
                className={`${inputOutline} ${inputText} h-[42.6px] pl-[15.8px] pr-9 py-[10.6px]`}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6E7670] dark:text-gray-400 text-[14px] font-['Segoe_UI'] leading-[21px] pointer-events-none">
                {discountSymbol}
              </span>
            </div>
            <FormErrorText message={errors.discount_value} />
          </div>

          {/* Points Required – h 42.6, px 15.8 */}
          <div className="flex-1 flex flex-col gap-2">
            <RequiredLabel htmlFor="coupon-points-required" className={labelClass} required>
              Points Required
            </RequiredLabel>
            <Input
              id="coupon-points-required"
              type="number"
              value={coupon.points_required || ""}
              onChange={(e) => onFieldChange("points_required", parseInt(e.target.value) || 0)}
              disabled={isCreating}
              placeholder="0"
              className={`${inputOutline} ${inputText} h-[42.6px] px-[15.8px] py-[10.6px]`}
            />
            <FormErrorText message={errors.points_required} />
          </div>

          {/* Total Quantity – py 11 px 16, placeholder " 1-100" */}
          <div className="flex-1 flex flex-col gap-2">
            <RequiredLabel htmlFor="coupon-total-quantity" className={labelClass} required>
              Total Quantity Available
            </RequiredLabel>
            <Input
              id="coupon-total-quantity"
              type="number"
              value={coupon.total_quantity_available || ""}
              onChange={(e) =>
                onFieldChange("total_quantity_available", parseInt(e.target.value) || 0)
              }
              disabled={isCreating}
              placeholder=" 1-100"
              className={`${inputOutline} ${inputText} h-[42.6px] py-[11px] px-4`}
            />
            <FormErrorText message={errors.total_quantity_available} />
          </div>
        </div>
      </div>
    </div>
  );
};
